import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

// Fixed flight paths so the server and the browser draw the same flock.
// top is a share of the hero height, rest is where each bird sits when motion is reduced.
const FLOCK = [
    { top: 17, size: 40, duration: 30, delay: -9, flap: 1.8, rest: '24%' },
    { top: 20.5, size: 30, duration: 30, delay: -8.2, flap: 1.6, rest: '19%' },
    { top: 14.5, size: 27, duration: 30, delay: -7.7, flap: 1.7, rest: '16%' },
    { top: 24, size: 32, duration: 38, delay: -25, flap: 2, rest: '66%' },
    { top: 26.5, size: 24, duration: 38, delay: -24.1, flap: 1.7, rest: '62%' },
    { top: 11, size: 22, duration: 46, delay: -34, flap: 1.9, rest: '80%' }
]

interface BirdsProps {
    className?: string
}

// Birds gliding across the sky: a few wing beats, then a glide.
export const Birds: FC<BirdsProps> = ({ className }): ReactNode => {
    return (
        <div
            aria-hidden
            className={cn('pointer-events-none absolute inset-0 overflow-hidden [--bird-scale:0.7] sm:[--bird-scale:1]', className)}
        >
            {FLOCK.map((bird, index) => (
                <span
                    key={index}
                    className={cn(
                        'absolute left-0 motion-safe:animate-fly motion-reduce:[left:var(--rest)]',
                        (index === 2 || index === 4) && 'max-sm:hidden'
                    )}
                    style={{
                        top: `${bird.top}%`,
                        animationDuration: `${bird.duration}s`,
                        animationDelay: `${bird.delay}s`,
                        '--rest': bird.rest
                    } as CSSProperties}
                >
                    <svg
                        viewBox='0 0 40 16'
                        fill='currentColor'
                        className='block origin-[50%_60%] text-[#27303b]/80 motion-safe:animate-flap'
                        style={{
                            width: `calc(${bird.size}px * var(--bird-scale))`,
                            height: `calc(${bird.size * 0.4}px * var(--bird-scale))`,
                            animationDuration: `${bird.flap}s`,
                            animationDelay: `${-index * 0.37}s`
                        }}
                    >
                        <path d='M0 6C6 1 12 1 20 9C28 1 34 1 40 6C33 4 27 6 20 13C13 6 7 4 0 6Z' />
                    </svg>
                </span>
            ))}
        </div>
    )
}
